import * as React from "react";
import {inject, observer} from "mobx-react";
import type {AppState} from "../../../data/state/AppState";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import {
	SettingsSubNav,
	NavigationEnum,
	SettingsPermissionsSubNav,
	SettingsModuleNav,
	SettingsModuleSubNav,
	PermissionSetSubNav,
} from "../../../Enums";
import {SettingOption} from "./SettingOption";
import type {ISettingsSubMenu} from "./SettingsComponentTree";
import {getSettingsComponentById, SettingsComponentTree} from "./SettingsComponentTree";

interface ISettingsViewProps {
	readonly appState?: AppState;
}

interface ISettingsViewState {
	matches?: boolean;
	openDropdownMenu: SettingsSubNav.MODULES | SettingsSubNav.PERMISSIONS | null;
}

@inject("appState")
@observer
export class SettingsView extends React.Component<ISettingsViewProps, ISettingsViewState> {
	constructor(props: ISettingsViewProps) {
		super(props);
		this.state = {
			matches: window.matchMedia("(max-width: 1366px) and (max-height: 768px)").matches,
			openDropdownMenu: null,
		};
	}

	private changeHandler = (ev: MediaQueryListEvent) => this.setState({matches: ev.matches});

	private forceUpdateArrow = () => {
		this.forceUpdate();
	};

	private onSelectOption = (
		id: SettingsSubNav | SettingsPermissionsSubNav | SettingsModuleNav,
		parentId: SettingsSubNav | SettingsPermissionsSubNav | SettingsModuleNav | "" = "",
	) => {
		this.setState({
			openDropdownMenu: null,
		});

		const {navigation} = this.props.appState.app;

		if (parentId === SettingsSubNav.MODULES) {
			// #app/<org-id>/settings/modules/xyicon/types
			const parts = StringUtils.decomposeParts(location.hash);
			let settingsPart: SettingsModuleSubNav = SettingsModuleSubNav.TYPES;

			if (parts[5]?.value && parts[4]?.value !== SettingsPermissionsSubNav.PERMISSION_SETS) {
				settingsPart = parts[5]?.value as SettingsModuleSubNav;
			}

			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [SettingsSubNav.MODULES, id, settingsPart],
			});
		} else if (parentId === SettingsSubNav.PERMISSIONS && id === SettingsPermissionsSubNav.PERMISSION_SETS) {
			// #app/<org-id>/settings/permissions/permissionsets/portfolios
			const parts = StringUtils.decomposeParts(location.hash);
			let settingsPart: PermissionSetSubNav = PermissionSetSubNav.PORTFOLIOS;

			if (parts[5]?.value && parts[4]?.value === SettingsPermissionsSubNav.PERMISSION_SETS) {
				settingsPart = parts[5]?.value as PermissionSetSubNav;
			}

			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [SettingsSubNav.PERMISSIONS, id, settingsPart],
			});
		} else {
			let activeTab: SettingsModuleSubNav = null;
			const mainSubNav: SettingsSubNav = Object.values(SettingsPermissionsSubNav).includes(id as unknown as SettingsPermissionsSubNav)
				? SettingsSubNav.PERMISSIONS
				: null;

			if (Object.values(SettingsModuleNav).includes(id as unknown as SettingsModuleNav)) {
				activeTab = location.hash.split("/").pop() as SettingsModuleSubNav;

				if (!Object.values(SettingsModuleSubNav).includes(activeTab)) {
					activeTab = SettingsModuleSubNav.TYPES;
				}
			}

			navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [mainSubNav, id, activeTab],
			});
		}
	};

	private isParentOpened(settingOption: ISettingsSubMenu) {
		const {matches: isMobileView} = this.state;

		if (!isMobileView && settingOption.children?.length > 0) {
			return true;
		}

		return this.state.openDropdownMenu === settingOption.id;
	}

	public override componentDidMount() {
		window.matchMedia("(max-width: 1366px) and (max-height: 768px)").addEventListener("change", this.changeHandler);
		window.addEventListener("hashchange", this.forceUpdateArrow);
	}

	public override componentDidUpdate(): void {
		const {selectedSubMenu} = this.props.appState;
		if (!selectedSubMenu) {
			this.props.appState.app.navigation.goApp({
				mainNav: NavigationEnum.SETTINGS,
				subNavs: [SettingsSubNav.PERSONAL],
			});
		}
	}

	public override componentWillUnmount() {
		window.matchMedia("(max-width: 1366px) and (max-height: 768px)").removeEventListener("change", this.changeHandler);
		window.removeEventListener("hashchange", this.forceUpdateArrow);
	}

	public override render() {
		const {appState} = this.props;
		const {selectedSubMenu, selectedSettingsModuleMenu, selectedPermissionMenu} = appState;
		const isUserAdmin = appState.user?.isAdmin;

		const selectedSubMenuIdWithComponent = selectedPermissionMenu || selectedSettingsModuleMenu || selectedSubMenu;

		if (!selectedSubMenuIdWithComponent) {
			return <></>;
		}

		return (
			<div className="SettingsView">
				<div className="tab">
					{SettingsComponentTree.map((settingOption, index) => {
						if ((!isUserAdmin && ["My Profile", "Organization"].includes(settingOption.label)) || isUserAdmin) {
							return (
								<div
									key={settingOption.id}
									className="SettingsContainer"
								>
									<SettingOption
										key={settingOption.id}
										label={settingOption.label}
										isGroup={settingOption.children?.length > 0}
										selected={!(settingOption.children?.length > 0) && selectedSubMenuIdWithComponent === settingOption.id}
										onClick={() => {
											if ([SettingsSubNav.MODULES, SettingsSubNav.PERMISSIONS].includes(settingOption.id as unknown as SettingsSubNav.PERMISSIONS)) {
												if (this.state.openDropdownMenu === settingOption.id) {
													this.setState({
														openDropdownMenu: null,
													});
												} else {
													this.setState({
														openDropdownMenu: settingOption.id as unknown as SettingsSubNav.PERMISSIONS,
													});
												}
											} else {
												this.onSelectOption(settingOption.id);
											}
										}}
										isParentOpened={this.isParentOpened(settingOption)}
									>
										{this.isParentOpened(settingOption) && (
											<div className="children">
												{settingOption.children.map((child, index) => (
													<SettingOption
														key={child.id}
														label={child.label}
														isGroup={child.children?.length > 0}
														selected={selectedSubMenuIdWithComponent === child.id}
														onClick={() => this.onSelectOption(child.id, settingOption.id)}
													/>
												))}
											</div>
										)}
									</SettingOption>
									{this.isParentOpened(settingOption) && (
										<div className="DropdownContent">
											{settingOption.children.map((child, index) => (
												<SettingOption
													key={child.id}
													label={child.label}
													isGroup={child.children?.length > 0}
													selected={selectedSubMenuIdWithComponent === child.id}
													onClick={() => {
														this.onSelectOption(child.id, settingOption.id);
													}}
												/>
											))}
										</div>
									)}
								</div>
							);
						}
					})}
				</div>
				<div className="content flex_1 vbox">{getSettingsComponentById(selectedSubMenuIdWithComponent)}</div>
			</div>
		);
	}
}
